import React from 'react';
import { Button } from 'antd';
import { DownOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TaskCard from './TaskCard';

/**
 * ستون کانبان وظایف — الگوی اسکرول عیناً مطابق KanbanColumn سرنخ/معامله:
 * ستون ارتفاع ثابت دارد و بدنه با flex:1 + height:0 اسکرول می‌شود.
 */
const TaskKanbanColumn = ({
  column,
  tasks = [],
  onTaskClick,
  onAddTask,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `column-${column.id}` });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `column-${column.id}`,
    data: { type: 'column', column },
  });

  const taskIds = tasks.map((t) => `task-${t.id}`);
  const color = column.label_color || column.color || '#1890ff';
  const title = column.column_name || column.title;
  const totalCount = Number(column.tasks_count ?? tasks.length) || 0;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={(node) => {
        setDragRef(node);
        setDropRef(node);
      }}
      style={{
        ...style,
        fontFamily: 'CustomFont',
        height: 'calc(100vh - 350px)',
        minHeight: '600px',
        maxHeight: 'calc(100vh - 350px)',
      }}
      className={`
        flex-shrink-0 w-80 bg-white dark:bg-gray-800 rounded-lg
        border border-gray-200 dark:border-gray-700
        flex flex-col
        transition-all duration-200
        ${isOver ? 'ring-2 ring-indigo-300 shadow-md' : ''}
        ${isDragging ? 'shadow-2xl scale-105' : 'shadow-sm hover:shadow-md'}
      `}
    >
      <div
        className="p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0"
        style={{ borderTop: `3px solid ${color}` }}
      >
        <div className="flex items-center justify-between gap-2 mb-0">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <button
              type="button"
              className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-0.5 shrink-0 bg-transparent border-0 touch-none"
              aria-label="جابجایی ستون"
              {...attributes}
              {...listeners}
            >
              <HolderOutlined />
            </button>
            <h3
              className="text-sm font-bold m-0 text-gray-800 dark:text-gray-200 truncate flex-1"
              style={{ fontFamily: 'CustomFont' }}
            >
              {title}
            </h3>
          </div>
          <span
            className="text-xs font-semibold px-1.5 py-0.5 rounded shrink-0"
            style={{
              fontFamily: 'CustomFont',
              backgroundColor: `${color}15`,
              color,
            }}
            title={tasks.length < totalCount ? `${tasks.length} از ${totalCount}` : undefined}
          >
            {totalCount}
          </span>
          {onAddTask ? (
            <Button
              type="text"
              icon={<PlusOutlined style={{ fontSize: '12px' }} />}
              onClick={() => onAddTask(column.id)}
              size="small"
              className="h-6 w-6 p-0 flex-shrink-0"
            />
          ) : null}
        </div>
      </div>

      <div
        className={`overflow-y-auto overflow-x-hidden p-3 kanban-column-scroll ${
          isOver ? 'bg-indigo-50/30 dark:bg-indigo-900/10' : ''
        }`}
        style={{
          flex: '1 1 0%',
          minHeight: 0,
          height: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {tasks.map((task) => (
              <TaskCard key={task.id} task={task} onClick={onTaskClick} />
            ))}
          </div>
        </SortableContext>

        {hasMore && onLoadMore ? (
          <Button
            type="default"
            block
            size="small"
            icon={<DownOutlined />}
            loading={loadingMore}
            onClick={onLoadMore}
            className="mt-2 mb-1 border-dashed text-blue-600 hover:text-blue-700 hover:border-blue-300"
            style={{ fontFamily: 'CustomFont' }}
          >
            مشاهده بیشتر
          </Button>
        ) : null}

        {tasks.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-xs text-gray-400 dark:text-gray-600 mb-2" style={{ fontFamily: 'CustomFont' }}>
              وظیفه‌ای وجود ندارد
            </p>
            {onAddTask ? (
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => onAddTask(column.id)}
                style={{ fontFamily: 'CustomFont' }}
              >
                افزودن وظیفه
              </Button>
            ) : null}
          </div>
        ) : onAddTask ? (
          <Button
            type="dashed"
            block
            size="small"
            icon={<PlusOutlined />}
            onClick={() => onAddTask(column.id)}
            className="mt-2"
            style={{ fontFamily: 'CustomFont' }}
          >
            افزودن وظیفه
          </Button>
        ) : null}
      </div>

      <style>{`
        .kanban-column-scroll {
          scrollbar-width: thin;
          scrollbar-color: ${color}40 transparent;
        }
        .kanban-column-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .kanban-column-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .kanban-column-scroll::-webkit-scrollbar-thumb {
          background: ${color}40;
          border-radius: 10px;
        }
        .kanban-column-scroll::-webkit-scrollbar-thumb:hover {
          background: ${color}80;
        }
      `}</style>
    </div>
  );
};

export default React.memo(TaskKanbanColumn);
